import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterNextRender,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { ResultStatus } from '../../models/test-run.model';
import { TEST_RUN_STATUS_ICON, TEST_RUN_TO_RES_PILL } from '../../shared/test-run-outcome';
import { ResPillComponent } from '../res-pill/res-pill.component';

/**
 * Was von einem abgeschlossenen Testlauf bleibt: Ergebnis, Zahlen und der Hinweis,
 * dass nichts mehr daran zu aendern ist.
 *
 * <p>Zusammengezogen aus zwei gleichen Umsetzungen — der Testlauf-Seite und dem
 * Durchlauf-Runner —, die bis auf Klassennamen und i18n-Zweig Deklaration fuer
 * Deklaration dasselbe waren.
 *
 * <p>Die Zahlen kommen fertig von aussen: die beiden Seiten rechnen sie aus
 * verschiedenen Quellen (Schritt-Ergebnisse des Laufs gegen die Faelle des
 * Durchlaufs), und auch die Dauer stammt einmal aus zwei Zeitstempeln und einmal aus
 * einem Millisekunden-Feld. Die Karte zeigt an, sie rechnet nicht.
 *
 * <p>Die Beschriftung des Status kommt ebenfalls von aussen. Sie steht in beiden
 * Seiten bereits uebersetzt bereit; eine dritte Tabelle derselben sechs Woerter
 * daneben koennte von ihnen abweichen.
 */
@Component({
  selector: 'app-run-summary-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, TranslatePipe, ResPillComponent],
  templateUrl: './run-summary-card.component.html',
  styleUrl: './run-summary-card.component.scss',
})
export class RunSummaryCardComponent {
  readonly status = input.required<ResultStatus>();

  /** Bereits uebersetzt — siehe Klassen-Doku. */
  readonly statusLabel = input.required<string>();

  readonly passed = input.required<number>();
  readonly total = input.required<number>();
  readonly failed = input.required<number>();
  /** Eine Kachel je Status (NEX-1496) — das Bundle fuehrt keine fuer Blockiert. */
  readonly blocked = input.required<number>();
  readonly skipped = input.required<number>();

  /** Bereits formatiert; leer wird zum Strich, damit keine Zelle leer bleibt. */
  readonly duration = input.required<string>();

  /** Der Weg zurueck — wohin, entscheidet die Seite. */
  readonly leave = output<void>();

  /**
   * Ob die Seite eine Ergebnis-Ansicht anbietet. Ein eigener Schalter, weil sich an
   * einem Output nicht ablesen laesst, ob jemand ihn gebunden hat.
   */
  readonly resultsAvailable = input(false);

  /** Der Weg zu den Ergebnissen — wohin, entscheidet die Seite. */
  readonly showResults = output<void>();

  /**
   * Ob der Lauf abgebrochen wurde (NEX-1315). Dann nennt die Pille den Abbruch statt des
   * Ergebnisses — ein abgebrochener Durchlauf endet ohne Ergebnis und hiesse sonst
   * „Uebersprungen".
   */
  readonly aborted = input(false);

  /**
   * Die Begruendung des Abbruchs. {@code null} heisst: es gibt keine (ein Abbruch aus
   * der Zeit vor der Pflicht-Begruendung). {@code undefined} heisst: sie ist nicht
   * bekannt, etwa weil ihr Laden scheiterte — dann behauptet die Karte auch keine.
   */
  readonly abortReason = input<string | null | undefined>(undefined);

  /** Ob die Karte beim Erscheinen den Fokus nimmt — etwa weil der Knopf, der sie ausloeste, fort ist. */
  readonly focusOnShow = input(false);

  /**
   * Die Pille nennt den Abbruch nur am Durchlauf, den der Abbruch geschlossen hat — der
   * endet ohne Ergebnis als SKIPPED. Ein frueherer, regulaer beendeter Durchlauf
   * desselben Laufs behaelt sein Ergebnis.
   */
  readonly abortedExecution = computed(() => this.aborted() && this.status() === 'SKIPPED');

  private readonly card = viewChild.required<ElementRef<HTMLElement>>('card');

  constructor() {
    afterNextRender(() => {
      if (this.focusOnShow()) this.card().nativeElement.focus();
    });
  }

  readonly icon = computed(() => TEST_RUN_STATUS_ICON[this.status()]);
  readonly pillStatus = computed(() => TEST_RUN_TO_RES_PILL[this.status()]);
  readonly modifier = computed(() => this.status().toLowerCase());
}
